import { translationKey, localize } from '../i18n';
import useRequestGuard from '../hooks/useRequestGuard';
import { stockErrors, stockSnapshot } from '../utils/stockValidation';
import SingleLineText from '../components/ui/InternationalText';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PageLoading from '../components/PageLoading';
import {
    Box, Typography, CircularProgress,
    Collapse, LinearProgress,
} from '@mui/material';
import MedicationIcon from '@mui/icons-material/Medication';
import MedImage from '../components/MedImage';

import PageHeader from '../components/PageHeader';
import AppToast from '../components/AppToast';
import { getDeviceUuid, getMedications, updateMedication } from '../api';
import { clearMedicationCaches } from '../utils/localCache';
import { isValidDosage } from './medication-form/reviewDraftError';
import AppButton from '../components/ui/AppButton';
import AppCard from '../components/ui/AppCard';
import AppField from '../components/ui/AppField';

function ToggleSwitch({ checked, onChange, label }) {
    return (
        <Box role="switch" aria-checked={checked} aria-label={label} tabIndex={0} onClick={onChange}
            onKeyDown={(event) => { if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); event.currentTarget.click(); } }} sx={{
            width: 50, height: 28, borderRadius: 14, cursor: 'pointer',
            bgcolor: checked ? '#ffe566' : '#d1d5db',
            position: 'relative', transition: 'all 0.25s ease', flexShrink: 0,
        }}>
            <Box sx={{
                width: 24, height: 24, borderRadius: '50%', bgcolor: 'white',
                position: 'absolute', top: 2, left: checked ? 24 : 2,
                transition: 'all 0.25s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
        </Box>
    );
}

export default function StockSettingsPage() {
    const beginRequest = useRequestGuard('stock_settings');
    const [medications, setMedications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [stockEditing, setStockEditing] = useState(null);
    const [stockForm, setStockForm] = useState({ totalStock: '', remainingStock: '', dosagePerTime: 1, stockAlertThreshold: 3 });
    const [stockSaving, setStockSaving] = useState(false);
    const mutationBusyRef = useRef(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchMedications = useCallback(async () => {
        const isCurrent = beginRequest('load');
        setLoading(true);
        try {
            const meds = await getMedications(getDeviceUuid());
            if (!isCurrent()) return;
            setMedications(meds);
            setLoadError(false);
        } catch {
            if (isCurrent()) setLoadError(true);
        } finally { if (isCurrent()) setLoading(false); }
    }, [beginRequest]);
    useEffect(() => { fetchMedications(); }, [fetchMedications]);

    const handleStockToggle = (med) => {
        if (med.totalStock) {
            handleStockDisable(med);
        } else {
            setStockEditing(med.id);
            setStockForm({ totalStock: '', remainingStock: '', dosagePerTime: med.dosagePerTime || 1, stockAlertThreshold: med.stockAlertThreshold ?? 3 });
        }
    };

    const [disablingId, setDisablingId] = useState(null);
    const handleStockDisable = async (med) => {
        if (mutationBusyRef.current) return;
        mutationBusyRef.current = true;
        const isCurrent = beginRequest('mutation');
        setDisablingId(med.id);
        try {
            const uuid = getDeviceUuid();
            const saved = await updateMedication(uuid, med.id, { totalStock: 0, remainingStock: 0, expectedStock: stockSnapshot(med) });
            clearMedicationCaches();
            if (!isCurrent()) return;
            setMedications(prev => prev.map(m => m.id === med.id ? { ...m, totalStock: null, remainingStock: null, ...saved } : m));
            setSnackbar({ open: true, message: localize("{v0} 재고 관리 해제", { v0: med.name }), severity: 'success' });
        } catch (error) { if (isCurrent()) await handleStockError(error, translationKey('변경 실패'), isCurrent); }
        finally { mutationBusyRef.current = false; setDisablingId(null); }
    };

    const handleStockError = async (error, fallback, isCurrent) => {
        if (error.response?.status === 409) {
            setStockEditing(null);
            await fetchMedications();
            if (!isCurrent()) return;
            setSnackbar({ open: true, message: localize("재고가 변경됐어요. 최신 수량을 확인하고 다시 수정해주세요"), severity: 'error' });
        } else setSnackbar({ open: true, message: error.response?.data?.message || fallback, severity: 'error' });
    };

    const errors = stockErrors(stockForm);
    const handleStockSave = async (medId) => {
        if (mutationBusyRef.current || Object.keys(stockErrors(stockForm)).length || !isValidDosage(stockForm.dosagePerTime)) return;
        mutationBusyRef.current = true;
        const isCurrent = beginRequest('mutation');
        setStockSaving(true);
        try {
            const uuid = getDeviceUuid();
            const totalStock = Number(stockForm.totalStock);
            const remaining = stockForm.remainingStock === ''
                ? totalStock
                : Number(stockForm.remainingStock);
            const original = medications.find(m => m.id === medId);
            const saved = await updateMedication(uuid, medId, {
                expectedStock: stockSnapshot(original),
                totalStock,
                remainingStock: remaining,
                dosagePerTime: Number(stockForm.dosagePerTime) || 1,
                stockAlertThreshold: Number(stockForm.stockAlertThreshold),
            });
            clearMedicationCaches();
            if (!isCurrent()) return;
            setMedications(prev => prev.map(m => m.id === medId ? {
                ...m,
                totalStock,
                remainingStock: remaining,
                dosagePerTime: Number(stockForm.dosagePerTime) || 1,
                stockAlertThreshold: Number(stockForm.stockAlertThreshold),
                ...saved,
            } : m));
            setStockEditing(null);
            setSnackbar({ open: true, message: localize("재고 설정 완료"), severity: 'success' });
        } catch (error) { if (isCurrent()) await handleStockError(error, translationKey('저장 실패'), isCurrent); }
        finally { mutationBusyRef.current = false; setStockSaving(false); }
    };

    if (loading) {
        return <PageLoading />;
    }

    return (
        <Box sx={{ pb: 10 }}>
            <PageHeader title={localize("재고 관리")} back />

            <Box sx={{ px: 2, pt: 2 }}>
                <Typography sx={{ fontSize: '0.9rem', color: '#aaa', mb: 2, px: 0.5 }}>{localize("약별로 재고를 설정하면 복용 시 자동 차감되고, 부족할 때 알려드려요")}</Typography>

                {loadError && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <SingleLineText sx={{ mb: 2 }}>{localize("재고 정보를 불러오지 못했어요")}</SingleLineText>
                        <AppButton onClick={fetchMedications}>{localize("다시 불러오기")}</AppButton>
                    </Box>
                )}
                {!loadError && medications.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <MedicationIcon sx={{ fontSize: 48, mb: 2, color: '#ddd' }} />
                        <SingleLineText sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#999' }}>{localize("등록된 약이 없어요")}</SingleLineText>
                        <SingleLineText sx={{ color: '#bbb', fontSize: '0.95rem', mt: 0.5 }}>{localize("약을 먼저 등록해주세요")}</SingleLineText>
                    </Box>
                )}

                {medications.map((med) => {
                    const hasStock = !!med.totalStock;
                    const stockPercent = hasStock && med.remainingStock != null
                        ? (med.remainingStock / med.totalStock) * 100 : null;
                    const isEditing = stockEditing === med.id;

                    return (
                        <AppCard key={med.id} kind="plain" sx={{
                            mb: 1.5, overflow: 'hidden',
                        }}>
                            <Box role="button" tabIndex={0} sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                p: 2, cursor: 'pointer',
                                '&:active': { opacity: 0.7 },
                            }} onClick={() => {
                                if (mutationBusyRef.current) return;
                                if (isEditing) { setStockEditing(null); return; }
                                handleStockToggle(med);
                            }}>
                                <MedImage src={med.imageUrl} size={40} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography data-medication-name-preview sx={{
                                        fontWeight: 700, fontSize: '1.05rem', color: '#1a1a1a',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>{med.name}</Typography>
                                    {hasStock && stockPercent !== null && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <LinearProgress variant="determinate" value={stockPercent}
                                                    sx={{
                                                        height: 5, borderRadius: 3, bgcolor: '#f0f0f0',
                                                        '& .MuiLinearProgress-bar': {
                                                            bgcolor: stockPercent < 20 ? '#ef4444' : '#ffe566', borderRadius: 3,
                                                        },
                                                    }} />
                                            </Box>
                                            <Typography sx={{
                                                fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap',
                                                color: stockPercent < 20 ? '#ef4444' : '#777',
                                            }}>
                                                {med.remainingStock}/{med.totalStock}{localize("정")}</Typography>
                                        </Box>
                                    )}
                                    {!hasStock && (
                                        <SingleLineText minFontSize={11.5} sx={{ fontSize: '0.88rem', color: '#aaa', mt: 0.2 }}>
                                            {isEditing ? localize("수량을 입력하고 저장해 주세요") : localize("터치하여 재고 관리 시작")}
                                        </SingleLineText>
                                    )}
                                </Box>
                                {disablingId === med.id ? <CircularProgress size={22} aria-label={localize("재고 관리 해제 중")} /> : <ToggleSwitch checked={hasStock || isEditing} label={localize("{v0} 재고 관리", { v0: med.name })} onChange={() => {}} />}
                            </Box>

	                            <Collapse in={isEditing}>
	                                <Box sx={{
                                        px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2,
                                        '&& .MuiInput-root': { borderBottom: 0 },
                                        '& .MuiInput-root:before, & .MuiInput-root:hover:not(.Mui-disabled):before': { borderBottom: '1px solid #dfe2e6' },
                                        '& .MuiInput-root:after': { borderBottom: 0 },
                                    }}>
	                                    <Box>
	                                        <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#333', mb: 0.5 }}>{localize("총 몇 정짜리인가요?")}</SingleLineText>
	                                        <AppField appearance="underline" disabled={stockSaving || disablingId !== null}
	                                            type="number" fullWidth placeholder={localize("예: 30")}
	                                            value={stockForm.totalStock}
                                            error={!!stockForm.totalStock && !!errors.totalStock}
                                            helperText={stockForm.totalStock ? localize(errors.totalStock) : undefined}
	                                            onChange={(e) => setStockForm(prev => ({
	                                                ...prev,
	                                                totalStock: e.target.value,
	                                            }))}
                                            inputProps={{ min: 0.01, max: 9999, step: '0.01', inputMode: 'decimal', style: { fontSize: '1.1rem', fontWeight: 600 } }}
                                        />
	                                    </Box>
	                                    <Box>
	                                        <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#333', mb: 0.5 }}>{localize("지금 남은 약은 몇 정인가요?")}</SingleLineText>
	                                        <AppField appearance="underline" disabled={stockSaving || disablingId !== null}
	                                            type="number" fullWidth placeholder={localize("예: 30")}
	                                            value={stockForm.remainingStock}
                                            error={!!errors.remainingStock}
	                                            onChange={(e) => setStockForm(prev => ({ ...prev, remainingStock: e.target.value }))}
                                            inputProps={{ min: 0, max: Number(stockForm.totalStock) || undefined, step: '0.01', inputMode: 'decimal', style: { fontSize: '1.1rem', fontWeight: 600 } }}
                                            helperText={localize(errors.remainingStock) || localize("비워 두면 총 수량으로 저장해요. 보충했다면 현재 남은 수량을 적어주세요")}
                                        />
	                                    </Box>
	                                    <Box>
                                        <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#333', mb: 0.5 }}>{localize("한 번에 몇 정 먹나요?")}</SingleLineText>
                                        <AppField appearance="underline" disabled={stockSaving || disablingId !== null}
                                            type="number" fullWidth
                                            value={stockForm.dosagePerTime}
                                            error={!isValidDosage(stockForm.dosagePerTime)}
                                            onChange={(e) => {
                                                const rawValue = e.target.value;
                                                setStockForm(prev => ({ ...prev, dosagePerTime: rawValue }));
                                            }}
                                            inputProps={{ min: 0.01, step: '0.01', inputMode: 'decimal', style: { fontSize: '1.1rem', fontWeight: 600 } }}
                                            helperText={!isValidDosage(stockForm.dosagePerTime) ? localize("1회 복용량은 0.01~99 사이로 소수 둘째 자리까지 입력해주세요") : localize("복용할 때마다 이 수량만큼 자동 차감돼요")}
                                        />
                                    </Box>
                                    <Box>
                                        <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#333', mb: 0.5 }}>{localize("남은 약이 몇 정 이하면 알려줄까요?")}</SingleLineText>
                                        <AppField appearance="underline" disabled={stockSaving || disablingId !== null}
                                            type="number" fullWidth placeholder={localize("예: 5")}
                                            value={stockForm.stockAlertThreshold}
                                            error={!!errors.stockAlertThreshold}
                                            onChange={(e) => setStockForm(prev => ({ ...prev, stockAlertThreshold: e.target.value }))}
                                            inputProps={{ min: 0, max: 9999, step: 1, inputMode: 'numeric', style: { fontSize: '1.1rem', fontWeight: 600 } }}
                                            helperText={localize(errors.stockAlertThreshold) || localize("이 수량 이하가 되면 알림을 보내요")}
                                        />
                                    </Box>
                                    <SingleLineText sx={{ fontSize: '0.8rem', color: '#777' }}>{localize("수량 변경 후에는 이전 기록 취소로 재고가 늘지 않아요")}</SingleLineText>
                                    <AppButton
                                        fullWidth variant="contained"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => handleStockSave(med.id)}
                                        disabled={stockSaving || disablingId !== null || Object.keys(errors).length > 0 || !isValidDosage(stockForm.dosagePerTime)}
                                        sx={{ py: 1.2, mt: 0.5 }}
                                    >
                                        {stockSaving ? <CircularProgress size={22} sx={{ color: '#5a4a00' }} /> : localize("저장")}
                                    </AppButton>
                                </Box>
                            </Collapse>

                            {hasStock && !isEditing && (
                                <Box
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                            if (mutationBusyRef.current) return;
	                                        setStockEditing(med.id);
	                                        setStockForm({
	                                            totalStock: String(med.totalStock),
	                                            remainingStock: String(med.remainingStock ?? med.totalStock),
	                                            dosagePerTime: med.dosagePerTime || 1,
	                                            stockAlertThreshold: med.stockAlertThreshold ?? 3,
                                        });
                                    }}
                                    sx={{
                                        px: 2, pb: 1.5, cursor: 'pointer',
                                        '&:active': { opacity: 0.6 },
                                    }}
                                >
                                    <SingleLineText sx={{ fontSize: '0.9rem', color: '#8a7000', fontWeight: 600, textAlign: 'center' }}>{localize("재고 수정")}</SingleLineText>
                                </Box>
                            )}
                        </AppCard>
                    );
                })}
            </Box>

            <AppToast open={snackbar.open} autoHideDuration={snackbar.severity === 'error' ? 4000 : 3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />
        </Box>
    );
}
